import React from "react";
import { Alert, Spinner } from "react-bootstrap";
import styled from "styled-components";
import { WidgetTheme } from "../../types/analytics";
import WidgetInfoButton from "./WidgetInfoButton";

const CardContainer = styled.div<{ bgcolor: string; bordercolor: string }>`
    position: relative;
    background-color: ${props => props.bgcolor};
    border: 1px solid ${props => props.bordercolor};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-right: 32px;
`;

interface WidgetCardProps {
    title?: string;
    description?: string;
    isLoading?: boolean;
    error?: string | null;
    isEmpty?: boolean;
    emptyMessage?: string;
    theme: WidgetTheme;
    children?: React.ReactNode;
    style?: React.CSSProperties;
    titleAdornment?: React.ReactNode;
}

const WidgetCard: React.FC<WidgetCardProps> = ({
    title,
    description,
    isLoading,
    error,
    isEmpty,
    emptyMessage = "No data available for the selected date range.",
    theme,
    children,
    style,
    titleAdornment
}) => {
    const header = title ? (
        <CardHeader>
            <h4 style={{ margin: 0, color: theme.text }}>{title}</h4>
            {titleAdornment}
        </CardHeader>
    ) : null;

    const infoIcon = description ? (
        <div style={{ position: "absolute", top: "12px", right: "12px" }}>
            <WidgetInfoButton description={description} placement="auto" />
        </div>
    ) : null;

    if (isLoading) {
        return (
            <CardContainer bgcolor={theme.chartBg} bordercolor={theme.border} style={style}>
                {header}
                {infoIcon}
                <div className="text-center" style={{ padding: "20px" }}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </div>
            </CardContainer>
        );
    }

    if (error) {
        return (
            <CardContainer bgcolor={theme.chartBg} bordercolor={theme.border} style={style}>
                {header}
                {infoIcon}
                <Alert variant="danger">{error}</Alert>
            </CardContainer>
        );
    }

    if (isEmpty) {
        return (
            <CardContainer bgcolor={theme.chartBg} bordercolor={theme.border} style={style}>
                {header}
                {infoIcon}
                <Alert variant="info">{emptyMessage}</Alert>
            </CardContainer>
        );
    }

    return (
        <CardContainer bgcolor={theme.chartBg} bordercolor={theme.border} style={style}>
            {header}
            {infoIcon}
            {children}
        </CardContainer>
    );
};

export default WidgetCard;
